import './MenuNavigationButtons.css';
import React, { type FC } from 'react';
import cx from 'classnames';
import { useUnmountReactAndNavigate } from '@src/hooks';
import { isOnVentionPendant } from '@src/Main/main';
import { messageBox } from '@src/style/style';
import { Pages } from '@src/Pages';
import { tabService } from '@src/TabService';

interface MenuNavigationButtonsProps {
  restrictAccess: boolean
  isMachineAppAvailable: boolean
}

enum MenuNavigationButtonSizeCSSClass {
  SMALL = 'small-grid-button',
  MEDIUM = 'medium-grid-button',
  LARGE = 'large-grid-button',
}

export const MenuNavigationButtons: FC<MenuNavigationButtonsProps> = ({ restrictAccess, isMachineAppAvailable }) => {
  if (isMachineAppAvailable) {
    return (
      <div className="menu-navigation-buttons-container">
        <div className='menu-navigation-buttons-grid machine-app-layout'>
          <div className={MenuNavigationButtonSizeCSSClass.LARGE}>
            <MachineAppMenuButton/>
          </div>
          <div className={MenuNavigationButtonSizeCSSClass.SMALL}>
            <MachineConfigurationMenuButton/>
          </div>
          <div className={MenuNavigationButtonSizeCSSClass.SMALL}>
            <JoggerMenuButton/>
          </div>
          <div className={MenuNavigationButtonSizeCSSClass.SMALL}>
            <NetworkConfigurationMenuButton/>
          </div>
          <div className={MenuNavigationButtonSizeCSSClass.SMALL}>
            <MachineLogicMenuButton restrictAccess={restrictAccess}/>
          </div>
          <div className={MenuNavigationButtonSizeCSSClass.SMALL}>
            <Cloud9MenuButton/>
          </div>
          <div className={MenuNavigationButtonSizeCSSClass.SMALL}>
            <AppLauncherMenuButton restrictAccess={restrictAccess}/>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="menu-navigation-buttons-container">
      <div className='menu-navigation-buttons-grid'>
        <MachineConfigurationMenuButton/>
        <JoggerMenuButton/>
        <NetworkConfigurationMenuButton/>
        <MachineLogicMenuButton restrictAccess={restrictAccess}/>
        <Cloud9MenuButton/>
        <AppLauncherMenuButton restrictAccess={restrictAccess}/>
      </div>
    </div>
  );
};

const MachineConfigurationMenuButton: FC = () => {
  const navigate = useUnmountReactAndNavigate();
  const navigateToMachineConfig = (): void => { navigate(Pages.MACHINE_CONFIG); };

  return <MenuNavigationButton
      iconClass={'icon-cogs'}
      onClick={navigateToMachineConfig}
    >
      <div> Machine <br/> Configuration</div>
    </MenuNavigationButton>;
};

const JoggerMenuButton: FC = () => {
  const navigate = useUnmountReactAndNavigate();
  const navigateToJogger = (): void => { navigate(Pages.JOGGER); };

  return <MenuNavigationButton
      iconClass={'icon-jogger_inactive'}
      onClick={navigateToJogger}
    >
      <div>Manual <br/> Control</div>
    </MenuNavigationButton>;
};

const NetworkConfigurationMenuButton: FC = () => {
  const navigate = useUnmountReactAndNavigate();
  const navigateToNetworkConfig = (): void => { navigate(Pages.NETWORK_CONFIG); };

  return <MenuNavigationButton
      iconClass={'icon-network-connection'}
      onClick={navigateToNetworkConfig}
    >
      <div>Network <br/> Connection</div>
    </MenuNavigationButton>;
};

interface MachineLogicMenuButtonProps {
  restrictAccess: boolean
}

const MachineLogicMenuButton: FC<MachineLogicMenuButtonProps> = ({ restrictAccess }) => {
  const navigate = useUnmountReactAndNavigate();
  const navigateToMachineLogic = (): void => { navigate(Pages.MACHINE_LOGIC); };
  const showMachineLogicUnavailableMessage = (): void => { messageBox('Please connect to the parent controller to make changes on the MachineLogic page', 'info'); };

  return <MenuNavigationButton
    iconClass={'icon-launch-sequence-editor'}
    onClick={restrictAccess ? showMachineLogicUnavailableMessage : navigateToMachineLogic}
    isRestricted={restrictAccess}
  >
    <div>MachineLogic</div>
  </MenuNavigationButton>;
};

const Cloud9MenuButton: FC = () => {
  const navigateToCloud9 = (): void => { window.open(`${location.origin}:3000`); };
  const showCloud9UnavailableMessage = (): void => { messageBox('Cloud9 is not available on the pendant', 'info'); };

  return <MenuNavigationButton
    iconClass={'icon-cloud9'}
    isRestricted={isOnVentionPendant()}
    onClick={isOnVentionPendant() ? showCloud9UnavailableMessage : navigateToCloud9}
  >
    <div>
      <div>Cloud9</div>
      <span className='cloud9-username-badge'>
        Username: debian
      </span>
    </div>
  </MenuNavigationButton>;
};

interface AppLauncherMenuButtonProps {
  restrictAccess: boolean
}

const AppLauncherMenuButton: FC<AppLauncherMenuButtonProps> = ({ restrictAccess }) => {
  const navigate = useUnmountReactAndNavigate();
  const navigateToAppLauncher = (): void => { navigate(Pages.APP_LAUNCHER); };
  const showAppLauncherUnavailableMessage = (): void => { messageBox('Please connect to the parent controller to access the App Launcher page.', 'info'); };

  return <MenuNavigationButton
      iconClass={'icon-launch'}
      onClick={restrictAccess ? showAppLauncherUnavailableMessage : navigateToAppLauncher}
      isRestricted={restrictAccess}
      >
        <div>Application <br/> Launcher</div>
      </MenuNavigationButton>;
};

const MachineAppMenuButton: FC = () => {
  const navigateToMachineApp = (): void => { tabService.switchTo('machine-app'); };

  return <MenuNavigationButton
    iconClass={'icon-machine-app'}
    onClick={navigateToMachineApp}
  >
    <div>Back to MachineApp</div>
  </MenuNavigationButton>;
};

interface MenuNavigationButtonProps {
  iconClass: string
  onClick: () => void
  isRestricted?: boolean | undefined
}

const MenuNavigationButton: FC<MenuNavigationButtonProps> = ({ iconClass, children, onClick, isRestricted }) => {
  return (
    <div
      className={cx('menu-navigation-button', {
        restricted: isRestricted,
      })}
      onClick={onClick}
    >
      <span className={`menu-navigation-button-icon ${iconClass}`}/>
      <div className={'menu-navigation-title'}> {children} </div>
    </div>
  );
};
